import React from "react";
import PropTypes from "prop-types";

import HelpTrigger from "@/components/help-trigger/HelpTrigger";
import { Indexer as IndexerType } from "@/components/proptypes";

import Form from "antd/lib/form";
import Button from "antd/lib/button";

import Title from "./Title";
import Query from "./Query";
import IndexerDestinations from "./IndexerDestinations";
import HorizontalFormItem from "../HorizontalFormItem";
import IndexerInsertStrategy from "./IndexerInsertStrategy";
import IndexerTargetTable from "./IndexerTargetTable";

export default class IndexerNew extends React.Component {
  state = {
    saving: false,
  };

  save = () => {
    this.setState({ saving: true });
    this.props.save().catch(() => {
      this.setState({ saving: false });
    });
  };

  handleDataSourceSelect = (dataSource) => {
    // Update the indexer with the selected data source
    if (dataSource) {
      this.props.onCriteriaChange({ 
        ...this.props.indexer.options,
        data_source_id: dataSource.id 
      });
    } else {
      // Handle deselection if needed
      const options = { ...this.props.indexer.options };
      delete options.data_source_id;
      this.props.onCriteriaChange(options);
    }
  };

  handleTargetTableChange = (e) => {
    // Use a more appropriate prop for updating non-criteria options
    this.props.onOptionsChange({
      target_table: e.target.value
    });
  };

  handleDeleteDuplicatesChange = (e) => {
    // Use a more appropriate prop for updating non-criteria options
    this.props.onOptionsChange({
      delete_duplicates: e.target.checked
    });
  };

  handleInsertStrategyChange = (e) => {
    // Update the insert_strategy option
    this.props.onOptionsChange({
      insert_strategy: e.target.value
    });
  };

  render() {
    const { indexer, queryResult, onQuerySelected, onNameChange, onCriteriaChange, onOptionsChange } = this.props;
    const { query, name, options } = indexer;
    const { saving } = this.state;

    return (
      <>
        <Title indexer={indexer} name={name} onChange={onNameChange} editMode />
        <div className="bg-white tiled p-20">
          <div className="d-flex">
            <Form className="flex-fill">
              <div className="m-b-30">
                Start by selecting the query that you would like to use for indexing data using the search bar.
                <br />
                Keep in mind that Indexers work best with queries that run periodically.
              </div>
              <HorizontalFormItem label="Query">
                <Query query={query} queryResult={queryResult} onChange={onQuerySelected} editMode />
              </HorizontalFormItem>
              {queryResult && options && (
                <>
                  <HorizontalFormItem label="Destination">
                    <IndexerDestinations 
                      indexerId={indexer.id || null} 
                      onDataSourceSelect={this.handleDataSourceSelect}
                    />
                  </HorizontalFormItem>
                  <HorizontalFormItem label="Target Table">
                    <IndexerTargetTable 
                      targetTable={options.target_table}
                      onChange={value => this.props.onOptionsChange({ target_table: value })}
                    />
                  </HorizontalFormItem>
                  <HorizontalFormItem label="Insert Strategy">
                    <IndexerInsertStrategy 
                      insertStrategy={options.insert_strategy}
                      onChange={value => this.props.onOptionsChange({ insert_strategy: value })}
                    />
                  </HorizontalFormItem>
                  <HorizontalFormItem label="Options">
                    <div className="form-check">
                      <input
                        id="deleteDuplicatesCheckbox"
                        type="checkbox"
                        className="form-check-input"
                        checked={!!options.delete_duplicates}
                        onChange={this.handleDeleteDuplicatesChange}
                      />
                      <label className="form-check-label" htmlFor="deleteDuplicatesCheckbox">
                        Remove duplicates when indexing
                      </label>
                    </div>
                  </HorizontalFormItem>
                </>
              )}
              <HorizontalFormItem>
                <Button type="primary" onClick={this.save} disabled={!query} className="btn-create-indexer">
                  {saving && (
                    <span role="status" aria-live="polite" aria-relevant="additions removals">
                      <i className="fa fa-spinner fa-pulse m-r-5" aria-hidden="true" />
                      <span className="sr-only">Saving...</span>
                    </span>
                  )}
                  Create Indexer
                </Button>
              </HorizontalFormItem>
            </Form>
            <HelpTrigger className="f-13" type="INDEXER_SETUP">
              Setup Instructions <i className="fa fa-question-circle" aria-hidden="true" />
              <span className="sr-only">(help)</span>
            </HelpTrigger>
          </div>
        </div>
      </>
    );
  }
}

IndexerNew.propTypes = {
  indexer: IndexerType.isRequired,
  queryResult: PropTypes.object,
  onQuerySelected: PropTypes.func.isRequired,
  save: PropTypes.func.isRequired,
  onNameChange: PropTypes.func.isRequired,
  onCriteriaChange: PropTypes.func.isRequired,
  onOptionsChange: PropTypes.func.isRequired,
};

IndexerNew.defaultProps = {
  queryResult: null,
};